/**
 * API Key 管理页。
 *
 * 列表展示 + 创建（一次性明文展示）+ 启用/禁用 + 删除。
 *
 * @module pages/ApiKeyPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';

import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import MoneyText from '@/components/common/MoneyText';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useApiKeys } from '@/hooks/useApiKeys';
import {
  createApiKey,
  deleteApiKey,
  updateApiKey,
  type ApiKeyWithSecret,
} from '@/services/apikey';
import type { ApiKey } from '@/types';

/** 待确认操作类型。 */
type ConfirmMode = 'delete' | 'disable';

/** 创建对话框表单状态。 */
interface CreateForm {
  name: string;
  quotaLimit: string;
  expiresAt: string;
}

/**
 * API Key 管理页组件。
 *
 * @returns 页面元素。
 */
export default function ApiKeyPage(): JSX.Element {
  const { data, loading, refetch } = useApiKeys();
  const { show, Toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>({
    name: '',
    quotaLimit: '',
    expiresAt: '',
  });
  const [creating, setCreating] = useState(false);

  const [createdKey, setCreatedKey] = useState<ApiKeyWithSecret | null>(null);
  const [confirm, setConfirm] = useState<{ mode: ConfirmMode; id: number } | null>(
    null,
  );
  const [acting, setActing] = useState(false);

  const rows: ApiKey[] = data ?? [];

  const resetForm = (): void => setForm({ name: '', quotaLimit: '', expiresAt: '' });

  const handleCreate = async (): Promise<void> => {
    if (!form.name.trim()) {
      show('请填写 Key 名称', 'warning');
      return;
    }
    setCreating(true);
    try {
      const result = await createApiKey({
        name: form.name.trim(),
        quotaLimit: form.quotaLimit ? form.quotaLimit : null,
        expiresAt: form.expiresAt ? dayjs(form.expiresAt).toISOString() : null,
      });
      setCreatedKey(result);
      resetForm();
      setCreateOpen(false);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '创建失败', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!confirm) return;
    setActing(true);
    try {
      if (confirm.mode === 'delete') {
        await deleteApiKey(confirm.id);
        show('已删除 API Key');
      } else {
        await updateApiKey(confirm.id, { status: 'disabled' });
        show('已禁用 API Key');
      }
      setConfirm(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '操作失败', 'error');
    } finally {
      setActing(false);
    }
  };

  const handleEnable = async (id: number): Promise<void> => {
    try {
      await updateApiKey(id, { status: 'active' });
      show('已启用 API Key');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '操作失败', 'error');
    }
  };

  const copyKey = async (): Promise<void> => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.keyPlain);
      show('已复制明文 Key');
    } catch {
      show('复制失败，请手动选择复制', 'warning');
    }
  };

  const columns: Column<ApiKey>[] = [
    { key: 'name', header: '名称' },
    { key: 'keyPrefix', header: 'Key 前缀' },
    {
      key: 'status',
      header: '状态',
      render: (row) =>
        row.status === 'active' ? (
          <StatusChip label="启用" color="success" />
        ) : (
          <StatusChip label="已禁用" color="default" />
        ),
    },
    {
      key: 'usedQuota',
      header: '已用额度',
      align: 'right',
      render: (row) => <MoneyText value={row.usedQuota} />,
    },
    {
      key: 'quotaLimit',
      header: '额度上限',
      align: 'right',
      render: (row) =>
        row.quotaLimit ? (
          <MoneyText value={row.quotaLimit} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            不限
          </Typography>
        ),
    },
    {
      key: 'expiresAt',
      header: '过期时间',
      render: (row) =>
        row.expiresAt ? (
          dayjs(row.expiresAt).format('YYYY-MM-DD')
        ) : (
          <Typography variant="body2" color="text.secondary">
            永不过期
          </Typography>
        ),
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          {row.status === 'active' ? (
            <Tooltip title="禁用">
              <IconButton
                size="small"
                color="warning"
                onClick={() => setConfirm({ mode: 'disable', id: row.id })}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="启用">
              <IconButton
                size="small"
                color="success"
                onClick={() => handleEnable(row.id)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="删除">
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirm({ mode: 'delete', id: row.id })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="API Keys"
        subtitle="管理你的调用密钥，请妥善保管明文 Key"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            新建 Key
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyText="还没有 API Key，点击右上角新建"
      />

      {/* 创建对话框 */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>新建 API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="名称"
            fullWidth
            margin="normal"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="额度上限（元，留空表示不限制）"
            fullWidth
            margin="normal"
            type="number"
            value={form.quotaLimit}
            onChange={(e) => setForm({ ...form, quotaLimit: e.target.value })}
          />
          <TextField
            label="过期时间（留空表示永不过期）"
            type="date"
            fullWidth
            margin="normal"
            value={form.expiresAt}
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            取消
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? '创建中...' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 一次性明文展示对话框 */}
      <Dialog
        open={Boolean(createdKey)}
        onClose={() => setCreatedKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key 创建成功</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            请立即复制并妥善保存，关闭后将无法再次查看明文。
          </Alert>
          <TextField
            fullWidth
            value={createdKey?.keyPlain ?? ''}
            InputProps={{ readOnly: true }}
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={copyKey} startIcon={<ContentCopyIcon />}>
            复制
          </Button>
          <Button onClick={() => setCreatedKey(null)} variant="contained">
            我已保存
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        danger
        title={confirm?.mode === 'delete' ? '删除 API Key' : '禁用 API Key'}
        message={
          confirm?.mode === 'delete'
            ? '删除后该 Key 将立即失效且不可恢复，确定删除？'
            : '禁用后该 Key 将停止服务，确定禁用？'
        }
        confirmText="确定"
        loading={acting}
        onConfirm={handleConfirm}
        onCancel={() => !acting && setConfirm(null)}
      />
      {Toast}
    </Box>
  );
}
